import { CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Modal, Rate, Switch, TableColumnsType, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { Testimonial } from '@common';
import { WrapperPage } from '@components/atoms';
import { GenericList } from '@components/molecules';
import { AddTestimonialModal } from '@components/molecules/AddTestimonialModal/AddTestimonialModal';
import { useTestimonials } from '@hooks';

const { Paragraph } = Typography;

enum OrderEnum {
  ascend = 'asc',
  descend = 'desc',
}

export const TestimonialsManagementPage = () => {
  // States to control modals
  const [testimonialFormModalOpen, setTestimonialFormModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState<Testimonial | null>(null);

  // States for pagination and search
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<string | undefined>('updatedAt');
  const [sortOrder, setSortOrder] = useState<string | undefined>('descend');

  // Get hooks
  const {
    getTestimonialsWithFilters,
    addTestimonial,
    updateTestimonial,
    deleteTestimonial,
    toggleTestimonialStatus,
  } = useTestimonials();

  // Parameters for testimonial search
  const params = {
    page,
    limit,
    search,
    orderBy: sortField,
    orderType: sortOrder ? OrderEnum[sortOrder as keyof typeof OrderEnum] : undefined,
  };

  // Fetch testimonials using the hook
  const { data, isLoading, refetch } = getTestimonialsWithFilters(params);

  // Refetch when params change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      refetch();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [page, limit, search, sortOrder, sortField, refetch]);

  // Check if data exists before accessing properties
  const testimonials = data?.data || [];
  const total = data?.pagination?.totalCount || 0;

  // Open modal to add a new testimonial
  const openAddTestimonialModal = () => {
    setSelectedTestimonial(null);
    setTestimonialFormModalOpen(true);
  };

  // Open modal to edit a testimonial
  const openEditTestimonialModal = (testimonial: Testimonial) => {
    setSelectedTestimonial(testimonial);
    setTestimonialFormModalOpen(true);
  };

  // Open modal to delete a testimonial
  const openDeleteTestimonialModal = (testimonial: Testimonial) => {
    setSelectedTestimonial(testimonial);
    setDeleteModalOpen(true);
  };

  // Handle testimonial save (add or update)
  const handleSaveTestimonial = async (testimonialData: any) => {
    try {
      if (selectedTestimonial) {
        // Update existing testimonial
        await updateTestimonial.mutateAsync({
          id: selectedTestimonial.id,
          data: testimonialData,
        });
      } else {
        // Add new testimonial
        await addTestimonial.mutateAsync(testimonialData);
      }
      setTestimonialFormModalOpen(false);
      refetch();
    } catch (error) {
      console.error('Error saving testimonial:', error);
    }
  };

  // Handle testimonial deletion
  const handleDeleteTestimonial = async () => {
    if (!selectedTestimonial) return;

    try {
      await deleteTestimonial.mutateAsync(selectedTestimonial.id);
      setDeleteModalOpen(false);
      refetch();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
    }
  };

  // Handle toggle status
  const handleToggleStatus = async (testimonial: Testimonial) => {
    try {
      await toggleTestimonialStatus.mutateAsync(testimonial.id);
      refetch();
    } catch (error) {
      console.error('Error toggling testimonial status:', error);
    }
  };

  // Handle search
  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  // Handle table changes (pagination, sorting)
  const handleTableChange = (pagination: any, _filters: unknown, sorter: any) => {
    const { current, pageSize } = pagination;

    if (pageSize !== limit) {
      setLimit(pageSize);
      setPage(1);
      return;
    }

    if (current !== page) {
      setPage(current);
      return;
    }

    if (sorter.columnKey !== sortField || sorter.order !== sortOrder) {
      setSortField(sorter.columnKey as string);
      setSortOrder(sorter.order as string);
      setPage(1);
    }
  };

  // Helper function to get avatar URL
  const getAvatarUrl = (testimonial: Testimonial) => {
    if (testimonial.avatar) {
      // Check if avatar is already a full URL
      if (testimonial.avatar.startsWith('http')) {
        return testimonial.avatar;
      }
      // Otherwise, construct the S3 URL
      const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'ruppert-production';
      const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-east-2';
      return `https://${bucketName}.s3.${region}.amazonaws.com/${testimonial.avatar}`;
    }
    return undefined;
  };

  // Table columns configuration
  const columns: TableColumnsType<Testimonial> = [
    {
      title: 'Avatar',
      dataIndex: 'avatar',
      key: 'avatar',
      width: 80,
      render: (_, record) => {
        const avatarUrl = getAvatarUrl(record);
        return avatarUrl ? (
          <img
            src={avatarUrl}
            alt={record.name}
            className="w-12 h-12 object-cover rounded-full"
            onError={(e) => {
              e.currentTarget.src = 'https://placehold.co/100x100?text=' + record.name.charAt(0);
            }}
          />
        ) : (
          <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center">
            <span className="text-white font-bold text-lg">
              {record.name.charAt(0).toUpperCase()}
            </span>
          </div>
        );
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (text: string) => (
        <span className="font-android text-white font-semibold">{text}</span>
      ),
    },
    {
      title: 'Job Title / Company',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => (
        <span className="font-android text-white">
          {text || '-'}
        </span>
      ),
    },
    {
      title: 'Rating',
      dataIndex: 'stars',
      key: 'stars',
      sorter: true,
      width: 150,
      render: (stars: number) => (
        <Rate disabled value={stars} className="!text-[#E3AF25] text-sm" />
      ),
    },
    {
      title: 'Testimonial',
      dataIndex: 'text',
      key: 'text',
      render: (text: string) => (
        <span className="font-android text-white">
          {text.length > 80 ? `${text.substring(0, 80)}...` : text}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean, record) => (
        <div className="flex items-center gap-2">
          <Switch
            checked={isActive}
            onChange={() => handleToggleStatus(record)}
            checkedChildren={<CheckCircleOutlined />}
            unCheckedChildren={<CloseCircleOutlined />}
          />
        </div>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      render: (date: string) => (
        <span className="font-android text-white">
          {dayjs(date).format('MMM DD, YYYY')}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <div className="flex gap-2">
          <Tooltip title="Edit testimonial">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openEditTestimonialModal(record);
              }}
              className="bg-yellow-principal !text-black hover:opacity-90"
            />
          </Tooltip>
          <Tooltip title="Delete testimonial">
            <Button
              icon={<DeleteOutlined />}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openDeleteTestimonialModal(record);
              }}
              className="!bg-red-500 !text-white !border-red-500 hover:!bg-red-600"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="overflow-x-hidden">
      <section className="bg-[url(/assets/images/Background.png)] bg-center bg-cover w-full py-12">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-6">
            <div className="font-android !font-normal md:!text-lg lg:!text-5xl text-yellow-principal">
              TESTIMONIALS MANAGEMENT
            </div>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              onClick={openAddTestimonialModal}
              className="bg-yellow-principal text-black font-android !text-xl w-50 !h-12 hover:opacity-90"
            >
              ADD TESTIMONIAL
            </Button>
          </div>

          <Paragraph className="!text-white font-android !text-xl tracking-wider">
            Manage customer testimonials - add, edit, or remove reviews
          </Paragraph>

          <WrapperPage isLoading={isLoading}>
            <GenericList<Testimonial>
              columns={columns}
              dataSource={testimonials}
              onRow={(record) => ({
                onClick: () => openEditTestimonialModal(record),
              })}
              onChange={handleTableChange}
              onSearch={handleSearch}
              pagination={{
                current: page,
                pageSize: limit,
                total: total,
                onChange(page, pageSize) {
                  setPage(page);
                  setLimit(pageSize || 10);
                },
                showSizeChanger: true,
              }}
            />
          </WrapperPage>

          {/* Modal for adding/editing testimonial */}
          <AddTestimonialModal
            isOpen={testimonialFormModalOpen}
            onCancel={() => setTestimonialFormModalOpen(false)}
            onSave={handleSaveTestimonial}
            initialValues={selectedTestimonial || undefined}
          />

          {/* Modal to confirm deletion */}
          <Modal
            title={<div className="!font-['Android_101'] !font-normal">DELETE TESTIMONIAL</div>}
            open={deleteModalOpen}
            onCancel={() => setDeleteModalOpen(false)}
            onOk={handleDeleteTestimonial}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{
              className: '!bg-red-500 !text-white !border-red-500 hover:!bg-red-600 font-android',
              loading: deleteTestimonial.isPending,
            }}
            cancelButtonProps={{
              className: 'font-android',
            }}
          >
            <Paragraph className="font-android">
              Are you sure you want to delete the testimonial from{' '}
              <strong>{selectedTestimonial?.name}</strong>? This action cannot be undone.
            </Paragraph>
          </Modal>
        </div>
      </section>
    </div>
  );
};

